import React from 'react';
import { Card } from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface ChartContainerProps {
  title?: React.ReactNode;
  type: 'line' | 'bar' | 'pie' | 'area' | 'scatter';
  data: any;
  config?: any;
  height?: number;
  loading?: boolean;
}

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'];

const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  type,
  data,
  config,
  height = 400,
  loading = false
}) => {
  const renderChart = () => {
    if (!data) return null;

    switch (type) {
      case 'line':
        // For line charts, transform Chart.js format to Recharts format
        const lineData = data.labels?.map((label: string, index: number) => {
          const point: any = { name: label };
          data.datasets?.forEach((dataset: any) => {
            point[dataset.label] = dataset.data[index];
          });
          return point;
        }) || [];

        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={lineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.datasets?.map((dataset: any, index: number) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={dataset.label}
                  stroke={dataset.borderColor || COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  fill={dataset.backgroundColor || 'none'}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        // For bar charts, transform Chart.js format to Recharts format
        const barData = data.labels?.map((label: string, index: number) => {
          const point: any = { name: label };
          data.datasets?.forEach((dataset: any) => {
            point[dataset.label] = dataset.data[index];
          });
          return point;
        }) || [];

        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.datasets?.map((dataset: any, index: number) => (
                <Bar
                  key={index}
                  dataKey={dataset.label}
                  fill={Array.isArray(dataset.backgroundColor) 
                    ? COLORS[index % COLORS.length] 
                    : dataset.backgroundColor || COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        // For pie charts, transform Chart.js format to Recharts format
        const pieData = data.labels?.map((label: string, index: number) => ({
          name: label,
          value: data.datasets?.[0]?.data[index] || 0
        })) || [];

        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'area':
        // For area charts, transform Chart.js format to Recharts format
        const areaData = data.labels?.map((label: string, index: number) => {
          const point: any = { name: label };
          data.datasets?.forEach((dataset: any) => {
            point[dataset.label] = dataset.data[index];
          });
          return point;
        }) || [];

        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={areaData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.datasets?.map((dataset: any, index: number) => (
                <Area
                  key={index}
                  type="monotone"
                  dataKey={dataset.label}
                  stackId="1"
                  stroke={dataset.borderColor || COLORS[index % COLORS.length]}
                  fill={dataset.backgroundColor || COLORS[index % COLORS.length]}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        // For scatter charts, we need a different data format
        const scatterData = data.datasets?.[0]?.data?.map((point: any, index: number) => ({
          x: point.x || index,
          y: point.y || point,
          name: data.labels?.[index] || `Point ${index + 1}`
        })) || [];

        return (
          <ResponsiveContainer width="100%" height={height}>
            <ScatterChart data={scatterData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" />
              <YAxis dataKey="y" />
              <Tooltip />
              <Scatter fill={COLORS[0]} />
            </ScatterChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div style={{ 
            height, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: '6px'
          }}>
            <span>Unsupported chart type: {type}</span>
          </div>
        );
    }
  };

  return (
    <Card title={title} loading={loading}>
      {renderChart()}
    </Card>
  );
};

export default ChartContainer; 